import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { addYears, startOfDay } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLocale } from "@/hooks/useLocale";
import { scheduledTimeLabel } from "../_lib/chat-labels";
import {
  combineDateAndTime,
  defaultCustomSendAt,
  schedulePresets,
  timeInputValue,
  validateSendAt,
  type SchedulePresetKey,
} from "../_lib/schedule-presets";

type Choice = SchedulePresetKey | "custom";

interface ScheduleDialogProps {
  open: boolean;
  /** Rescheduling: the current time, prefilled as the custom choice (keep it stable). */
  initial?: Date | null;
  isSubmitting?: boolean;
  onClose: () => void;
  /** A valid time was picked; the caller closes the dialog once it's done. */
  onConfirm: (sendAt: Date) => void;
}

const CUSTOM_ID = "chat-schedule-custom";

/** "When should it go out?" — quick picks plus a custom date and time. */
export const ScheduleDialog = ({
  open,
  initial,
  isSubmitting,
  onClose,
  onConfirm,
}: ScheduleDialogProps) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  // Captured on open so the preset times don't drift while it's on screen.
  const [now, setNow] = useState(() => new Date());
  const [choice, setChoice] = useState<Choice>("custom");
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [customTime, setCustomTime] = useState("");

  const presets = useMemo(() => schedulePresets(now), [now]);

  useEffect(() => {
    if (!open) return;
    const at = new Date();
    setNow(at);
    const start = initial ?? defaultCustomSendAt(at);
    setCustomDate(startOfDay(start));
    setCustomTime(timeInputValue(start));
    setChoice(initial ? "custom" : (schedulePresets(at)[0]?.key ?? "custom"));
  }, [open, initial]);

  const customAt = combineDateAndTime(customDate, customTime);
  const selectedAt =
    choice === "custom" ? customAt : (presets.find((p) => p.key === choice)?.at ?? null);
  const error = validateSendAt(selectedAt, now);

  const errorText =
    error === "past"
      ? t("scheduled.validation.past")
      : error === "too_far"
        ? t("scheduled.validation.too_far")
        : error === "invalid"
          ? t("scheduled.validation.invalid")
          : null;

  const submit = () => {
    // Re-checked against the clock: the dialog may have sat open a while.
    if (!selectedAt || validateSendAt(selectedAt, new Date())) {
      setNow(new Date());
      return;
    }
    onConfirm(selectedAt);
  };

  const presetLabel = (key: SchedulePresetKey) =>
    ({
      later_today: t("scheduled.presets.later_today"),
      tomorrow_morning: t("scheduled.presets.tomorrow_morning"),
      tomorrow_afternoon: t("scheduled.presets.tomorrow_afternoon"),
      next_monday: t("scheduled.presets.next_monday"),
    })[key];

  const rowClass =
    "flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 transition-colors hover:bg-muted has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary-muted";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initial ? t("scheduled.reschedule_title") : t("scheduled.schedule_title")}
          </DialogTitle>
          <DialogDescription>{t("scheduled.dialog_description")}</DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={choice}
          onValueChange={(v) => setChoice(v as Choice)}
          className="gap-2"
          aria-label={t("scheduled.when")}
        >
          {presets.map((p) => {
            const id = `chat-schedule-${p.key}`;
            return (
              <label key={p.key} htmlFor={id} className={rowClass}>
                <RadioGroupItem id={id} value={p.key} />
                <span className="flex-1 text-sm font-medium text-foreground">
                  {presetLabel(p.key)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {scheduledTimeLabel(t, formatDate, p.at, now)}
                </span>
              </label>
            );
          })}
          <label htmlFor={CUSTOM_ID} className={rowClass}>
            <RadioGroupItem id={CUSTOM_ID} value="custom" />
            <span className="flex-1 text-sm font-medium text-foreground">
              {t("scheduled.presets.custom")}
            </span>
          </label>
        </RadioGroup>

        {choice === "custom" ? (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>{t("scheduled.date")}</Label>
                <DatePickerInput
                  size="sm"
                  value={customDate}
                  minDate={startOfDay(now)}
                  maxDate={addYears(now, 1)}
                  onChange={setCustomDate}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="chat-schedule-time">{t("scheduled.time")}</Label>
                <Input
                  id="chat-schedule-time"
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>
            {errorText ? (
              <p role="alert" className="text-xs text-danger">
                {errorText}
              </p>
            ) : customAt ? (
              <p className="text-xs text-muted-foreground">
                {t("scheduled.sends_at", {
                  time: scheduledTimeLabel(t, formatDate, customAt, now),
                })}
              </p>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t("composer.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={!!error} isLoading={isSubmitting}>
            {initial ? t("scheduled.reschedule") : t("scheduled.schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
